import { useEffect, useRef, useState, useCallback } from "react";
import { ArrowRight, ArrowsClockwise, CursorClick, ArrowsOut, ArrowsIn, Camera } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { api } from "../lib/api";
import { atualizarPagina, navegarParaURL } from "../lib/api";

// Mapeamento de teclas do DOM para nomes aceitos por chromedp.KeyEvent
const TECLAS_ESPECIAIS_MAP = {
    Enter: "\r",
    Tab: "\t",
    Backspace: "\b",
    Escape: "\u001b",
    Delete: "\u007f",
    ArrowUp: "\u0001",
    ArrowDown: "\u0002",
    ArrowLeft: "\u0003",
    ArrowRight: "\u0004",
};

// Modificadores CDP: Alt=1, Control=2, Meta=4, Shift=8
function calcularModificadores(ev) {
    let m = 0;
    if (ev.altKey) m |= 1;
    if (ev.ctrlKey) m |= 2;
    if (ev.metaKey) m |= 4;
    if (ev.shiftKey) m |= 8;
    return m;
}

// Converte coords do click no img (object-contain) para coords reais da página renderizada
function mapearCoordenadas(ev, img, frame) {
    const rect = img.getBoundingClientRect();
    if (!frame || rect.width === 0 || rect.height === 0) return null;
    const escala = Math.min(rect.width / frame.largura, rect.height / frame.altura);
    const exibidaW = frame.largura * escala;
    const exibidaH = frame.altura * escala;
    const offsetX = (rect.width - exibidaW) / 2;
    const offsetY = (rect.height - exibidaH) / 2;
    const xRel = ev.clientX - rect.left - offsetX;
    const yRel = ev.clientY - rect.top - offsetY;
    if (xRel < 0 || xRel > exibidaW || yRel < 0 || yRel > exibidaH) return null;
    return { x: xRel / escala, y: yRel / escala };
}

// Throttle de mouse-move (envio ao backend). Mouse move pode disparar 100Hz+;
// 50ms (~20Hz) é suave o suficiente e libera a rede/CPU.
const THROTTLE_MOUSE_MS = 50;
// Throttle de scroll também — agrupa deltas pequenos.
const THROTTLE_SCROLL_MS = 30;

export default function AbaNavegador({ botId, bot, frameRef, frameTick }) {
    const [url, setUrl] = useState(bot.url_alvo || "");
    const [focado, setFocado] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const refImagem = useRef(null);
    const refContainer = useRef(null);
    const ultimoMouseMove = useRef(0);
    const scrollAcumuladoRef = useRef({ x: 0, y: 0, dx: 0, dy: 0, last: 0, timer: null });
    const [temFrame, setTemFrame] = useState(false);
    const rodando = bot.status === "rodando";

    // Atualiza o src da <img> DIRETAMENTE no DOM quando frameTick muda.
    // Evita que React reconcile a árvore inteira a cada frame (30fps).
    useEffect(() => {
        const f = frameRef.current;
        const img = refImagem.current;
        if (!f || !f.imagem) return;
        if (!temFrame) setTemFrame(true);
        if (img) {
            img.src = `data:image/jpeg;base64,${f.imagem}`;
        }
    }, [frameTick, frameRef, temFrame]);

    const irParaURL = async (e) => {
        e?.preventDefault();
        if (!url) return;
        if (!rodando) {
            toast.error("Inicie o bot primeiro");
            return;
        }
        try {
            await navegarParaURL(botId, url);
        } catch (e) {
            toast.error(e?.response?.data?.erro || "Falha");
        }
    };

    const atualizar = async () => {
        if (!rodando) return toast.error("Bot não está rodando");
        try { await atualizarPagina(botId); } catch { toast.error("Falha ao atualizar"); }
    };

    // Baixa screenshot atual do navegador como PNG com nome aleatório.
    const tirarPrint = async () => {
        if (!rodando) return toast.error("Bot não está rodando");
        try {
            const { data } = await api.get(`/bots/${botId}/screenshot`);
            const base64 = data?.imagem;
            if (!base64) {
                toast.error("Nenhuma imagem recebida");
                return;
            }
            // Converte base64 -> Blob -> link de download
            const bytes = atob(base64);
            const buffer = new Uint8Array(bytes.length);
            for (let i = 0; i < bytes.length; i++) buffer[i] = bytes.charCodeAt(i);
            const blob = new Blob([buffer], { type: "image/png" });
            const urlObj = URL.createObjectURL(blob);
            const rnd = Math.random().toString(36).slice(2, 10);
            const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
            const nome = `print_${ts}_${rnd}.png`;
            const a = document.createElement("a");
            a.href = urlObj;
            a.download = nome;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(urlObj), 2000);
            toast.success(`Print salvo: ${nome}`);
        } catch (e) {
            toast.error(e?.response?.data?.erro || "Falha ao tirar print");
        }
    };

    // Toggle de fullscreen usando Fullscreen API nativa do browser.
    const alternarFullscreen = useCallback(() => {
        const el = refContainer.current;
        if (!el) return;
        if (document.fullscreenElement) {
            document.exitFullscreen().catch(() => {});
        } else {
            el.requestFullscreen().catch(() => toast.error("Falha ao entrar em tela cheia"));
        }
    }, []);

    // Sincroniza estado quando ESC sai do fullscreen
    useEffect(() => {
        const onChange = () => setFullscreen(!!document.fullscreenElement);
        document.addEventListener("fullscreenchange", onChange);
        return () => document.removeEventListener("fullscreenchange", onChange);
    }, []);

    // Envia eventos para backend (silencioso - sem toasts).
    // Usa fetch direto com keepalive em vez de axios para minimizar overhead.
    const enviar = useCallback((caminho, dados) => {
        // Fire-and-forget. Erros são ignorados (interações são "best effort").
        api.post(`/bots/${botId}/${caminho}`, dados).catch(() => {});
    }, [botId]);

    const aoClicar = (ev) => {
        if (!rodando || !frameRef.current) return;
        const coords = mapearCoordenadas(ev, refImagem.current, frameRef.current);
        if (!coords) return;
        const botao = ev.button === 2 ? "right" : ev.button === 1 ? "middle" : "left";
        enviar("clicar", { ...coords, botao, modificadores: calcularModificadores(ev) });
    };

    const aoMoverMouse = (ev) => {
        if (!rodando || !frameRef.current || !focado) return;
        const agora = performance.now();
        if (agora - ultimoMouseMove.current < THROTTLE_MOUSE_MS) return;
        ultimoMouseMove.current = agora;
        const coords = mapearCoordenadas(ev, refImagem.current, frameRef.current);
        if (!coords) return;
        enviar("mover-mouse", coords);
    };

    const aoRolar = (ev) => {
        if (!rodando || !frameRef.current || !focado) return;
        ev.preventDefault();
        const coords = mapearCoordenadas(ev, refImagem.current, frameRef.current);
        if (!coords) return;
        // Throttle: acumula deltas e despacha em batch
        const acc = scrollAcumuladoRef.current;
        acc.x = coords.x;
        acc.y = coords.y;
        acc.dx += ev.deltaX;
        acc.dy += ev.deltaY;
        const agora = performance.now();
        if (agora - acc.last < THROTTLE_SCROLL_MS) {
            // Programa um flush atrasado
            if (!acc.timer) {
                acc.timer = setTimeout(() => {
                    acc.timer = null;
                    acc.last = performance.now();
                    enviar("rolar", { x: acc.x, y: acc.y, delta_x: acc.dx, delta_y: acc.dy });
                    acc.dx = 0; acc.dy = 0;
                }, THROTTLE_SCROLL_MS);
            }
            return;
        }
        acc.last = agora;
        enviar("rolar", { x: acc.x, y: acc.y, delta_x: acc.dx, delta_y: acc.dy });
        acc.dx = 0; acc.dy = 0;
    };

    // Captura teclado quando o navegador está focado
    useEffect(() => {
        if (!focado || !rodando) return;

        const aoTeclar = async (ev) => {
            // Não interceptar atalhos do navegador hospedeiro (F5, Ctrl+R = reload).
            if (ev.key === "F5" || (ev.ctrlKey && (ev.key === "r" || ev.key === "R"))) return;

            const modificador = ev.ctrlKey || ev.metaKey; // Ctrl (Win/Linux) ou Cmd (Mac)

            // Atalhos de clipboard: copiar / colar / recortar / selecionar-tudo.
            if (modificador) {
                const tecla = ev.key.toLowerCase();

                // COLAR (Ctrl/Cmd+V): lê o clipboard LOCAL do usuário e insere no
                // elemento focado do navegador remoto (Input.insertText).
                if (tecla === "v") {
                    ev.preventDefault();
                    try {
                        const texto = await navigator.clipboard.readText();
                        if (texto) enviar("colar", { texto });
                    } catch {
                        toast.error("Permita o acesso à área de transferência para colar");
                    }
                    return;
                }

                // COPIAR (Ctrl/Cmd+C): pega a seleção da página remota e grava no
                // clipboard LOCAL do usuário.
                if (tecla === "c") {
                    ev.preventDefault();
                    try {
                        const { data } = await api.get(`/bots/${botId}/selecao`);
                        const texto = data?.texto || "";
                        if (texto) {
                            await navigator.clipboard.writeText(texto);
                            toast.success("Copiado");
                        }
                    } catch { /* best effort */ }
                    return;
                }

                // RECORTAR (Ctrl/Cmd+X): copia a seleção remota para o clipboard
                // local e depois apaga a seleção no remoto (Delete).
                if (tecla === "x") {
                    ev.preventDefault();
                    try {
                        const { data } = await api.get(`/bots/${botId}/selecao`);
                        const texto = data?.texto || "";
                        if (texto) {
                            await navigator.clipboard.writeText(texto);
                            enviar("teclar", { tecla: TECLAS_ESPECIAIS_MAP.Delete });
                        }
                    } catch { /* best effort */ }
                    return;
                }

                // SELECIONAR TUDO (Ctrl/Cmd+A) no elemento focado remoto.
                if (tecla === "a") {
                    ev.preventDefault();
                    enviar("selecionar-tudo", {});
                    return;
                }

                // Outros atalhos com Ctrl/Cmd: não repassa (evita comportamento estranho).
                return;
            }

            // Teclas especiais (Enter, Tab, setas, etc.)
            if (TECLAS_ESPECIAIS_MAP[ev.key]) {
                ev.preventDefault();
                enviar("teclar", { tecla: TECLAS_ESPECIAIS_MAP[ev.key] });
                return;
            }

            // Caractere imprimível (1 char)
            if (ev.key.length === 1) {
                ev.preventDefault();
                enviar("digitar", { texto: ev.key });
            }
        };

        // Evento de "paste" nativo — cobre o colar via menu de contexto (botão direito)
        // e em navegadores que disparam paste mesmo fora de campos editáveis.
        const aoColar = (ev) => {
            const texto = ev.clipboardData?.getData("text");
            if (texto) {
                ev.preventDefault();
                enviar("colar", { texto });
            }
        };

        window.addEventListener("keydown", aoTeclar);
        window.addEventListener("paste", aoColar);
        return () => {
            window.removeEventListener("keydown", aoTeclar);
            window.removeEventListener("paste", aoColar);
        };
    }, [focado, rodando, enviar, botId]);

    // Bloqueia menu de contexto sobre a imagem
    const aoContexto = (ev) => {
        ev.preventDefault();
        aoClicar(ev);
    };

    return (
        <div className="space-y-3">
            <form onSubmit={irParaURL} className="flex items-center gap-2">
                <Input
                    data-testid="input-url-navegacao"
                    placeholder="https://..."
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    className="bg-zinc-900 border-zinc-800 focus:border-zinc-600 focus:ring-1 focus:ring-zinc-600 font-mono text-xs flex-1"
                />
                <Button type="submit" size="sm" data-testid="btn-ir-url"
                    className="bg-white text-zinc-950 hover:bg-zinc-200">
                    <ArrowRight size={14} weight="bold" />
                </Button>
                <Button type="button" size="sm" variant="outline" data-testid="btn-refresh-navegador"
                    className="bg-transparent border-zinc-800 hover:bg-zinc-800"
                    onClick={atualizar}>
                    <ArrowsClockwise size={14} />
                </Button>
                <Button type="button" size="sm" variant="outline" data-testid="btn-print-navegador"
                    className="bg-transparent border-zinc-800 hover:bg-zinc-800"
                    onClick={tirarPrint}
                    title="Tirar print e baixar PNG">
                    <Camera size={14} />
                </Button>
                <Button type="button" size="sm" variant="outline" data-testid="btn-fullscreen-navegador"
                    className="bg-transparent border-zinc-800 hover:bg-zinc-800"
                    onClick={alternarFullscreen}
                    title={fullscreen ? "Sair da tela cheia (ESC)" : "Tela cheia"}>
                    {fullscreen ? <ArrowsIn size={14} /> : <ArrowsOut size={14} />}
                </Button>
            </form>

            <div
                ref={refContainer}
                tabIndex={0}
                onFocus={() => setFocado(true)}
                onBlur={() => setFocado(false)}
                onWheel={aoRolar}
                onContextMenu={aoContexto}
                className={`relative w-full ${fullscreen ? "h-screen bg-black" : "aspect-video"} bg-black border rounded-md overflow-hidden flex items-center justify-center outline-none transition-colors ${focado ? "border-emerald-500/50 ring-1 ring-emerald-500/30" : "border-zinc-800"}`}
                data-testid="visualizador-navegador"
            >
                {/* <img> permanece SEMPRE montada; src é setado direto via ref (sem reconcile React).
                    Quando ainda não há frame, mostramos o placeholder por cima. */}
                <img
                    ref={refImagem}
                    alt="navegador"
                    onClick={aoClicar}
                    onMouseMove={aoMoverMouse}
                    draggable={false}
                    className={`max-w-full max-h-full cursor-crosshair object-contain select-none ${temFrame ? "" : "hidden"}`}
                />
                {!temFrame && (
                    <div className="text-center text-zinc-600 p-6">
                        <CursorClick size={32} className="mx-auto mb-3 opacity-50" />
                        <p className="text-sm">
                            {rodando ? "Aguardando primeiro frame do navegador…" : "Inicie o bot para abrir o navegador remoto"}
                        </p>
                    </div>
                )}
                <div className="absolute top-2 right-2 bg-black/60 backdrop-blur text-[10px] px-2 py-1 rounded-full border border-white/10 flex items-center gap-2 font-mono">
                    <span className={`w-2 h-2 rounded-full ${rodando ? "bg-emerald-500 pulso" : "bg-zinc-600"}`} />
                    {rodando ? "AO VIVO" : "OFFLINE"}
                </div>
                {!focado && rodando && temFrame && (
                    <div className="absolute bottom-2 left-2 bg-black/70 backdrop-blur text-[10px] px-2 py-1 rounded-md border border-white/10 font-mono text-zinc-300 pointer-events-none">
                        Clique aqui para ativar teclado/scroll
                    </div>
                )}
            </div>
        </div>
    );
}
